import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { createSystem, getSystem, updateSystem } from '../../services/systemService';
import { getRelativeFileUrl } from '../../services/relativeFilesService';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import CropIcon from '@mui/icons-material/Crop';
import SystemLogoDialog from './components/SystemLogoDialog';
import ImageIcon from '@mui/icons-material/Image';

interface SystemFormState {
  name: string;
  description?: string;
  base_url: string;
}

const SystemForm: React.FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const isEditMode = Boolean(systemId);
  const [formData, setFormData] = useState<SystemFormState>({
    name: '',
    description: '',
    base_url: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [openLogoDialog, setOpenLogoDialog] = useState<boolean>(false);

  useEffect(() => {
    if (isEditMode && systemId) {
      // Buscar dados do sistema para edição
      const fetchSystem = async () => {
        setLoading(true);
        try {
          const data = await getSystem(systemId);
          setFormData({
            name: data.name,
            description: data.description || '',
            base_url: data.base_url,
          });
          if (data.logo_url) {
            // Ajuste: usar helper para exibir caminho completo
            setLogoPreview(getRelativeFileUrl(data.logo_url));
          }
        } catch (error) {
          console.error('Erro ao buscar sistema:', error);
          swal('Erro', 'Não foi possível buscar os detalhes do sistema.', 'error');
        } finally {
          setLoading(false);
        }
      };
      fetchSystem();
    }
  }, [isEditMode, systemId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação básica
    if (!formData.name || !formData.base_url) {
      swal('Erro', 'Nome e URL Base são obrigatórios.', 'error');
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && systemId) {
        // Atualizar sistema
        await updateSystem(systemId, { ...formData, logo: logoFile || undefined });
        swal('Sucesso', 'Sistema atualizado com sucesso.', 'success');
      } else {
        // Criar novo sistema
        await createSystem({ ...formData, logo: logoFile || undefined });
        swal('Sucesso', 'Sistema criado com sucesso.', 'success');
      }
      navigate('/sistemas');
    } catch (error) {
      console.error('Erro ao salvar sistema:', error);
      const message = isEditMode
        ? 'Não foi possível atualizar o sistema.'
        : 'Não foi possível criar o sistema.';
      swal('Erro', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Verificar se o usuário tem permissão
  if (!user?.is_system_admin) {
    return (
      <Typography variant="h6" color="error">
        Você não tem permissão para acessar esta página.
      </Typography>
    );
  }

  // Callback chamado pelo <SystemLogoDialog> após recortar a imagem
  const handleLogoUpload = (file: File) => {
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? 'Editar Sistema' : 'Adicionar Sistema'}
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            margin="normal"
          />
          <TextField
            label="URL Base"
            name="base_url"
            value={formData.base_url}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
          />

          {/* Se for edição e houver logo existente, exibir preview */}
          {isEditMode && logoPreview && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Box
                component="img"
                src={logoPreview}
                alt="Logo do Sistema"
                sx={{
                  width: 80,
                  height: 80,
                  mr: 2,
                  objectFit: 'contain',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
              <Button
                variant="outlined"
                startIcon={<CropIcon />}
                onClick={() => setOpenLogoDialog(true)}
              >
                Alterar Logo
              </Button>
            </Box>
          )}

          {/* Botão para adicionar logo se não houver */}
          {!isEditMode && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Tooltip title="Adicionar Logo">
                <IconButton onClick={() => setOpenLogoDialog(true)}>
                  <ImageIcon />
                </IconButton>
              </Tooltip>
              {logoPreview && (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo do Sistema"
                  sx={{
                    width: 80,
                    height: 80,
                    ml: 2,
                    objectFit: 'contain',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
              )}
            </Box>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/sistemas')} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Diálogo para upload e corte da logo */}
      <SystemLogoDialog
        open={openLogoDialog}
        onClose={() => setOpenLogoDialog(false)}
        onCropComplete={handleLogoUpload}
      />
    </Box>
  );
};

export default SystemForm;